import React, { useMemo } from 'react';
import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Button 
} from '@mui/material';

import _ from 'lodash';
import { makeStyles } from '@mui/styles';
const useStyles = makeStyles({
  container: { padding: '16px', backgroundColor: '#ffffff' },
  headerTitle: { fontWeight: 600, color: '#000000', marginBottom: '20px' },
  searchArea: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  tableContainer: { 
    borderRadius: '1px', 
    backgroundColor: '#f8f9fa', 
    border: '1px solid #cbd5e0', 
    boxShadow: '0 4px 12px rgba(0,0,0,0.03)', 
    overflow: 'hidden' 
  },
  table: {
    width: '100%', 
    borderCollapse: 'collapse', 
    tableLayout: 'fixed',
    borderTop: '1px solid #cbd5e0 !important',
    
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #cbd5e0 !important', 
      borderRight: '1px solid #cbd5e0 !important',
      borderLeft: '1px solid #cbd5e0 !important',
      padding: '10px 14px !important', 
      fontSize: '0.875rem !important',
      fontFamily: 'Pretendard, sans-serif !important',
      fontVariantNumeric: 'tabular-nums !important',
      backgroundColor: '#ffffff',
    },
  },

  firstColumn: {
    backgroundColor: '#f5f5f5 !important',
    fontWeight: '700 !important',
    textAlign: 'center !important',
    fontSize: '0.9rem !important',
    borderRight: '1px solid #cbd5e0 !important',
  },

  headerCell: { 
    backgroundColor: '#e3f2fd !important', 
    color: '#000000 !important', 
    fontWeight: '600 !important', 
    textAlign: 'center !important',
    borderBottom: '2px solid #cbd5e0 !important',
    fontSize: '0.9rem !important', 
  },

  sumRow: { 
    '& .MuiTableCell-root': { 
      backgroundColor: '#edf2f7 !important', 
      color: '#000000',            
      fontWeight: '600 !important',
      borderBottom: '1px solid #cbd5e0 !important',
      fontSize: '0.85rem !important', 
    },
  },
  
  performanceHeader: { backgroundColor: '#fff3e0 !important', color: '#000000 !important', borderBottom: '2px solid #cbd5e0 !important' },
  remainHeader: { backgroundColor: '#e8f5e9 !important', color: '#000000 !important', borderBottom: '2px solid #cbd5e0 !important' },
  amountCell: { textAlign: 'right !important' },
  rateCell: { textAlign: 'center !important' },
  boldCell: { fontWeight: '700 !important' },
  highlightOrange: { backgroundColor: '#ffd8a8  !important'},
  saveBtn: { backgroundColor: '#3182ce !important', color: '#fff !important', borderRadius: '8px !important', padding: '10px 40px !important', fontWeight: '700 !important' }
});

const BudgetToActualComponents = ({ deptList, searchYear, startMonth, endMonth }) => {
  const classes = useStyles();

  const processedData = deptList;
  const totals = useMemo(() => {
    const data = processedData || [];

    // 1. 공통적으로 사용될 합계 항목들을 계산
    const totalBudget = _.sumBy(data, 'totalBudget') || 0;
    const totalActual = _.sumBy(data, 'totalActual') || 0;

    return {
      totalBudget: totalBudget,
      direct: _.sumBy(data, 'directActual') || 0,
      common: _.sumBy(data, 'commonActual') || 0,
      strategy: _.sumBy(data, 'strategyActual') || 0,
      totalActual: totalActual,
      totalRate:  Math.round(totalActual / totalBudget * 100),
      // 2. 미리 계산된 변수를 활용해 잔액 계산
      remain: totalBudget - totalActual 
    };
  }, [processedData]);

  return (
    <Box className={classes.container}>
      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table className={classes.table} size="small">
          <TableHead>
            {/* 1단 헤더 */}
            <TableRow>
              <TableCell rowSpan={2} className={classes.firstColumn}>부서</TableCell>
              <TableCell rowSpan={2} className={classes.headerCell}>년간 예산</TableCell>
              <TableCell colSpan={5} className={`${classes.headerCell} ${classes.performanceHeader}`}>집행 실적 ({searchYear}년 {startMonth}월 ~ {endMonth}월)</TableCell>
              <TableCell rowSpan={2} className={`${classes.headerCell} ${classes.remainHeader}`}>예산 잔액</TableCell>
            </TableRow>
            {/* 2단 헤더 */}
            <TableRow>
              <TableCell className={`${classes.headerCell} ${classes.performanceHeader}`}>직접비</TableCell>
              <TableCell className={`${classes.headerCell} ${classes.performanceHeader}`}>공통비</TableCell>
              <TableCell className={`${classes.headerCell} ${classes.performanceHeader}`}>전략본부 공통비</TableCell>
              <TableCell className={`${classes.headerCell} ${classes.performanceHeader} ${classes.highlightOrange}`}>집행 합계</TableCell>
              <TableCell className={`${classes.headerCell} ${classes.performanceHeader} ${classes.highlightOrange}`}>집행률</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((row) => {
              // 잔액 계산 및 반올림
              const execRate = Math.round((row.totalActual || 0) / (row.totalBudget || 0) * 100);
              const remainValue = Math.round((row.totalBudget || 0) - (row.totalActual || 0));
              
              return (
                <TableRow key={row.id}>
                  <TableCell align="center" className={classes.firstColumn}>{row.dept}</TableCell>
                  <TableCell className={classes.amountCell}>{Math.round(row.totalBudget || 0).toLocaleString()}</TableCell>
                  <TableCell className={classes.amountCell}>{Math.round(row.directActual || 0).toLocaleString()}</TableCell>
                  <TableCell className={classes.amountCell}>{Math.round(row.commonActual || 0).toLocaleString()}</TableCell>
                  <TableCell className={classes.amountCell}>{Math.round(row.strategyActual || 0).toLocaleString()}</TableCell>
                  <TableCell className={classes.amountCell}>{Math.round(row.totalActual || 0).toLocaleString()}</TableCell>
                  <TableCell className={classes.rateCell}>{(execRate || 0)}%</TableCell>
                  {/* 예산 잔액 반올림 적용 */}
                  <TableCell className={`${classes.amountCell} ${classes.boldCell}`}>
                    {remainValue.toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
            
            {/* 합계행 */}
            {processedData[0]?.isMyDept && (
              <TableRow className={classes.sumRow}>
                <TableCell align="center">합계</TableCell>
                <TableCell className={classes.amountCell} sx={{ color: '#0052cc' }}>{(Math.round(totals.totalBudget || 0)).toLocaleString()}</TableCell>
                <TableCell className={classes.amountCell}>{totals.direct.toLocaleString()}</TableCell>
                <TableCell className={classes.amountCell}>{totals.common.toLocaleString()}</TableCell>
                <TableCell className={classes.amountCell}>{totals.strategy.toLocaleString()}</TableCell>
                <TableCell className={classes.amountCell}>{totals.totalActual.toLocaleString()}</TableCell>
                <TableCell className={classes.rateCell}>{totals.totalRate}%</TableCell>
                <TableCell className={classes.amountCell} sx={{ color: '#0052cc' }}>{Math.round(totals.remain).toLocaleString()}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BudgetToActualComponents;
